import { REG_IN_PROGRESS_COOKIE } from "@/constants/common";
import { clearRegistrationOtpSendState } from "@/helpers/registrationOtpSend";
import { Details } from "@/types/userDetails";
import {
  getTabSession,
  removeTabSession,
  setTabSession,
} from "@/utils/tabSession";

import { regScreen } from "@/components/registration/map";

export const REGISTRATION_SESSION_VERSION = 1;
export const REGISTRATION_SESSION_NAMESPACE_PREFIX = "registration";

export type RegistrationSessionData = {
  screenType: string;
  details: Details;
  userData: Record<string, unknown> | null;
  progressPercent: number;
  isSkipabl: boolean;
  isResumeUploaded: boolean;
  formLevel: number;
};

const FIRST_REGISTRATION_SCREEN = "MOBILE_NUMBER_EMAIL";

const isBrowserDocument = (): boolean => typeof document !== "undefined";

const setRegistrationInProgressCookie = (): void => {
  if (!isBrowserDocument()) return;

  try {
    document.cookie = `${REG_IN_PROGRESS_COOKIE}=1; path=/; SameSite=Lax`;
  } catch (error) {
    console.error("Failed to set registration in-progress cookie", error);
  }
};

const clearRegistrationInProgressCookie = (): void => {
  if (!isBrowserDocument()) return;

  try {
    document.cookie = `${REG_IN_PROGRESS_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  } catch (error) {
    console.error("Failed to clear registration in-progress cookie", error);
  }
};

const syncRegistrationInProgressCookie = (screenType: string): void => {
  if (screenType === "SUCCESS") {
    clearRegistrationInProgressCookie();
    return;
  }

  if (screenType !== FIRST_REGISTRATION_SCREEN) {
    setRegistrationInProgressCookie();
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNonEmptyRecord = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) && Object.keys(value).length > 0;

export const getRegistrationSessionNamespace = (flowType: string): string =>
  `${REGISTRATION_SESSION_NAMESPACE_PREFIX}:${flowType || "default"}`;

export const isValidRegistrationScreen = (screenType: unknown): boolean =>
  typeof screenType === "string" &&
  (regScreen as readonly string[]).includes(screenType);

export const hydrateDetailsFromUserData = (
  details: Details,
  userData: unknown,
): Details => {
  const currentDetails = details ?? {};
  if (
    currentDetails.email ||
    currentDetails.mobileNumber ||
    currentDetails.fullName
  ) {
    return currentDetails;
  }

  if (!isRecord(userData)) return currentDetails;

  const personalDetails = (
    userData.personalDetailSection as Record<string, unknown> | undefined
  )?.personalDetails as Record<string, unknown> | undefined;

  if (!personalDetails) return currentDetails;

  const emails = personalDetails.emails as
    Array<{ email?: string }> | undefined;
  const mobileNumbers = personalDetails.mobileNumbers as
    Array<{ mobileNumber?: string; countryCode?: string }> | undefined;
  const email = emails?.[0]?.email;
  const mobile = mobileNumbers?.[0];

  return {
    ...currentDetails,
    email: currentDetails.email ?? email,
    mobileNumber: currentDetails.mobileNumber ?? mobile?.mobileNumber,
    countryCode:
      currentDetails.countryCode ??
      (mobile?.countryCode
        ? `+${mobile.countryCode}`
        : currentDetails.countryCode),
    fullName: currentDetails.fullName ?? (personalDetails.fullName as string),
  };
};

export const hasInProgressRegistrationSession = (flowType: string): boolean => {
  const session = getRegistrationSession(flowType);
  return Boolean(
    session &&
    session.screenType !== FIRST_REGISTRATION_SCREEN &&
    session.screenType !== "SUCCESS",
  );
};

export const getRegistrationSession = (
  flowType: string,
): RegistrationSessionData | null => {
  const stored = getTabSession<RegistrationSessionData>(
    getRegistrationSessionNamespace(flowType),
    REGISTRATION_SESSION_VERSION,
  );

  if (!stored || !isValidRegistrationScreen(stored.screenType)) {
    if (stored) {
      clearRegistrationSession(flowType);
    }
    return null;
  }

  const userData = isRecord(stored.userData) ? stored.userData : {};
  const details = hydrateDetailsFromUserData(
    isRecord(stored.details) ? (stored.details as Details) : {},
    userData,
  );

  return {
    screenType: stored.screenType,
    details,
    userData,
    progressPercent:
      typeof stored.progressPercent === "number" ? stored.progressPercent : 0,
    isSkipabl: Boolean(stored.isSkipabl),
    isResumeUploaded: Boolean(stored.isResumeUploaded),
    formLevel: typeof stored.formLevel === "number" ? stored.formLevel : 0,
  };
};

export const saveRegistrationSession = (
  flowType: string,
  snapshot: RegistrationSessionData,
): boolean => {
  if (!isValidRegistrationScreen(snapshot.screenType)) {
    return false;
  }

  if (snapshot.screenType === "SUCCESS") {
    clearRegistrationSession(flowType);
    return true;
  }

  const namespace = getRegistrationSessionNamespace(flowType);
  const existing = getTabSession<RegistrationSessionData>(
    namespace,
    REGISTRATION_SESSION_VERSION,
  );

  let nextUserData: Record<string, unknown> = {};
  if (isNonEmptyRecord(snapshot.userData)) {
    nextUserData = snapshot.userData;
  } else if (isNonEmptyRecord(existing?.userData)) {
    nextUserData = existing.userData;
  }

  let nextDetails: Details = {};
  if (isNonEmptyRecord(snapshot.details)) {
    nextDetails = snapshot.details as Details;
  } else if (isNonEmptyRecord(existing?.details)) {
    nextDetails = existing.details as Details;
  }

  const saved = setTabSession<RegistrationSessionData>(
    namespace,
    {
      screenType: snapshot.screenType,
      details: nextDetails as Details,
      userData: nextUserData as Record<string, unknown>,
      progressPercent: snapshot.progressPercent,
      isSkipabl: snapshot.isSkipabl,
      isResumeUploaded: snapshot.isResumeUploaded,
      formLevel: snapshot.formLevel,
    },
    REGISTRATION_SESSION_VERSION,
  );

  if (saved) {
    syncRegistrationInProgressCookie(snapshot.screenType);
  }

  return saved;
};

export const clearRegistrationSession = (flowType: string): void => {
  removeTabSession(getRegistrationSessionNamespace(flowType));
  clearRegistrationOtpSendState(flowType);
  clearRegistrationInProgressCookie();
};
